import React from "react";
import { useTranslation } from "react-i18next";
import Modal from "@mui/material/Modal";
import Box from "@mui/material/Box";
import {
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import Button from "@mui/material/Button";
import { ETheme } from "../../../shared/enums/user/ETheme";
import { darkPalette, lightPalette } from "../../../shared/mui/palettes";
import { RootState } from "../../../store/store";
import { useSelector } from "react-redux";
import { IUser } from "../../../shared/interfaces/user/IUser";
import { Controller, Resolver, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  EEmailCategory,
  ICreateEmailList,
} from "../../../shared/interfaces/email-list/IEmailList";
import { ELanguage } from "../../../shared/enums/user/ELanguage";
import * as yup from "yup";
import Grid from "@mui/material/Grid2";
import { LoadingButton } from "@mui/lab";
import { AdminEmailListService } from "../service";
import { handleError } from "../../../shared/functions/handleError";
import { enqueueSnackbar } from "notistack";

interface IProps {
  open: boolean;
  handleClose: () => void;
}

export const createEmailSchema = yup.object({
  name: yup.string().required("validation.required"),
  file: yup.mixed().required("validation.required"),
  language: yup
    .mixed<ELanguage>()
    .oneOf(Object.values(ELanguage))
    .required("validation.required"),
  category: yup
    .mixed<EEmailCategory>()
    .oneOf(Object.values(EEmailCategory))
    .required("validation.required"),
});

const EmailListUploadModal = ({ open, handleClose }: IProps) => {
  const { t } = useTranslation();
  const { theme } = useSelector(
    (state: RootState) => state?.auth?.userData,
  ) as IUser;
  const {
    handleSubmit,
    control,
    reset,
    formState: { isSubmitting },
  } = useForm<ICreateEmailList>({
    resolver: yupResolver(
      createEmailSchema,
    ) as unknown as Resolver<ICreateEmailList>,
    defaultValues: {
      category: EEmailCategory.MEDICAL,
      language: ELanguage.GB,
    } as unknown as ICreateEmailList,
  });
  const submitHandler = async (formState: ICreateEmailList) => {
    const formData = new FormData();
    const keys = Object.keys(formState);
    for (const key of keys) {
      formData.append(key, formState[key as keyof ICreateEmailList]);
    }
    await AdminEmailListService.upload(formData)
      .then((response) => {
        if (response) {
          enqueueSnackbar(
            t("toasts.successfullyCreated", { title: formState?.name }),
            {
              variant: "success",
            },
          );
          handleClose();
          reset({
            category: EEmailCategory.MEDICAL,
            language: ELanguage.GB,
          });
        }
      })
      .catch(handleError);
  };
  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Box
        className={"MuiModalBody"}
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          width: { xs: "80%", lg: "30%" },
          justifyContent: "center",
        }}
      >
        <Box
          sx={{
            width: "100%",
            paddingBottom: "10px",
            borderBottom: `1px solid ${theme === ETheme.LIGHT ? lightPalette.primary.light : darkPalette.background.light}`,
          }}
        >
          <Typography id="modal-modal-title" variant="h6">
            {t("emails.uploadList")}
          </Typography>
        </Box>
        <Box sx={{ marginTop: "30px", marginBottom: "30px", width: "100%" }}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <Controller
                name={"name"}
                control={control}
                render={({
                  field: { onChange, value },
                  fieldState: { error },
                }) => (
                  <TextField
                    error={!!error?.message}
                    helperText={t(error?.message || "")}
                    value={value ?? ""}
                    onChange={onChange}
                    label={t("emails.name")}
                    fullWidth
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Controller
                name={"category"}
                control={control}
                render={({
                  field: { onChange, value },
                  fieldState: { error },
                }) => (
                  <FormControl fullWidth>
                    <InputLabel
                      className={!!error?.message?.length ? "Mui-error" : ""}
                      id="category-label"
                    >
                      {t("emails.category")}
                    </InputLabel>
                    <Select
                      error={!!error?.message}
                      labelId="category-label"
                      id="category-select"
                      value={value ?? ""}
                      label={t("emails.category")}
                      onChange={onChange}
                    >
                      {Object.values(EEmailCategory)?.map((category) => {
                        return (
                          <MenuItem key={category} value={category}>
                            {category}
                          </MenuItem>
                        );
                      })}
                    </Select>
                    {!!error?.message?.length && (
                      <FormHelperText>{t(error?.message)}</FormHelperText>
                    )}
                  </FormControl>
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Controller
                name={"language"}
                control={control}
                render={({
                  field: { onChange, value },
                  fieldState: { error },
                }) => (
                  <FormControl fullWidth>
                    <InputLabel
                      className={!!error?.message?.length ? "Mui-error" : ""}
                      id="language-label"
                    >
                      {t("emails.language")}
                    </InputLabel>
                    <Select
                      error={!!error?.message}
                      labelId="language-label"
                      id="language-select"
                      value={value ?? ""}
                      label={t("emails.language")}
                      onChange={onChange}
                    >
                      {Object.values(ELanguage)?.map((language) => {
                        return (
                          <MenuItem key={language} value={language}>
                            {t(`language.${language}`)}
                          </MenuItem>
                        );
                      })}
                    </Select>
                    {!!error?.message?.length && (
                      <FormHelperText>{t(error?.message)}</FormHelperText>
                    )}
                  </FormControl>
                )}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Controller
                name={"file"}
                control={control}
                render={({ field: { onChange }, fieldState: { error } }) => (
                  <TextField
                    error={!!error?.message}
                    helperText={t(error?.message || "")}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      onChange(e.target.files?.[0] ?? null);
                    }}
                    label={t("emails.file")}
                    fullWidth
                    slotProps={{
                      inputLabel: { shrink: true },
                      input: {
                        inputProps: {
                          accept:
                            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                        },
                      },
                    }}
                    type="file"
                  />
                )}
              />
            </Grid>
          </Grid>
        </Box>
        <div>
          <Button
            variant={"contained"}
            color={"secondary"}
            size={"small"}
            onClick={handleClose}
            sx={{ marginRight: "10px" }}
          >
            {t("button.cancel")}
          </Button>

          <LoadingButton
            loading={isSubmitting}
            variant={"contained"}
            onClick={handleSubmit(submitHandler)}
          >
            {t("button.create")}
          </LoadingButton>
        </div>
      </Box>
    </Modal>
  );
};
export default EmailListUploadModal;
